import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Helmet } from 'react-helmet-async';
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../components/Auth";
import { AppContext } from "../components/AppProvider";
import { httpsCallable } from "firebase/functions";
import { getAuth, sendEmailVerification } from "firebase/auth";
import { collection, query, where, and, or, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, GeoPoint, orderBy } from 'firebase/firestore';

import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LoopIcon from '@mui/icons-material/Loop';

const AboutPage = () => {
    const { authUser, authInstance, firestoreInstance, functionsInstance, storageInstance } = useContext(AuthContext);
    const COLORS = useMemo(() => ({
        logoBackground: '#FFD1A3',
        camera: '#2E3434',
        coral: '#FF6B6B',
        orange: '#FF9F43',
        teal: '#00C2A8',
        purple: '#7B61FF',
        sunshine: '#FFD166',
        cream: '#FFF8F2',
        navy: '#14202B',
        gold: '#D4AF37'
    }), []);
    const navigate = useNavigate();
    const { config } = useContext(AppContext) || {};

    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        if (!firestoreInstance) return;
        setLoading(true);
        // Any data fetching or initialization logic can go here
        setLoading(false);
    }, [firestoreInstance, functionsInstance, authUser, authInstance, storageInstance]);

    return (
        <Box>
            <Helmet>
                <title>About MonadX — MonadX</title>
                <meta name="description" content="About MonadX — mission, features, and how we help you turn ingredients into meals." />
                <meta property="og:title" content="About MonadX" />
            </Helmet>
            {/* Intro Section */}
            <Box sx={{ bgcolor: COLORS.cream, minHeight: 'auto' }}>
                <Box sx={{ py: { xs: 6, md: 8 }, background: `linear-gradient(135deg, ${COLORS.coral}, ${COLORS.purple}, ${COLORS.sunshine})`, color: '#fff' }}>
                    <Container maxWidth="lg">
                        <Typography variant="h2" fontWeight={900} align="center" sx={{ mb: 1 }}>
                            About MonadX
                        </Typography>
                        <Typography variant="h5" fontWeight={700} align="center" sx={{ mb: 2, opacity: 0.95 }}>
                            MonadX — Direct Message campaign engine for X
                        </Typography>
                        <Typography variant="body1" align="center" sx={{ opacity: 0.95 }}>
                            Empower creators and teams to build, automate, and monetize audience engagement on X through high-conversion DM funnels.
                        </Typography>
                    </Container>
                </Box>
            </Box>

            {/* Mission Section */}
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Typography variant="h4" gutterBottom sx={{ color: COLORS.navy, fontWeight: 800 }}>
                Our Mission
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.navy, opacity: 0.85 }}>
                MonadX helps creators, founders, and marketers turn their X presence into a measurable growth channel. Automate opt-ins, deliver targeted DMs, and use analytics to optimize conversions and engagement.
                </Typography>
            </Container>

            <Divider />

            {/* Core Features Section */}
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Typography variant="h4" align="center" gutterBottom sx={{ color: COLORS.navy, fontWeight: 800 }}>
                What MonadX Does
                </Typography>
                <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ mb: 4 }}>
                {[{
                    id: 1,
                    icon: <PsychologyIcon />,
                    bg: COLORS.coral,
                    title: 'Automated Opt-ins',
                    body: 'Capture subscribers through replies, likes, or links and enroll them into DM funnels.'
                },{
                    id: 2,
                    icon: <FavoriteIcon />,
                    bg: COLORS.teal,
                    title: 'Targeted Messaging',
                    body: 'Send timed and behavior-driven DMs to increase conversions and lifetime value.'
                },{
                    id: 3,
                    icon: <LoopIcon />,
                    bg: COLORS.purple,
                    title: 'Analytics & Retargeting',
                    body: 'Track opens, clicks, and conversions; retarget audiences based on actions.'
                }].map((s) => (
                    <Grid item xs={12} sm={6} md={4} key={s.id} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Card elevation={0} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, p: { xs: 1, md: 2 }, width: { xs: '100%', md: 360 }, height: { xs: 'auto', md: 160 }, border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2, bgcolor: '#fff' }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: 1.5, bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {React.cloneElement(s.icon, { sx: { fontSize: 26, color: '#fff' } })}
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { md: '120px' } }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: COLORS.navy }}>{s.title}</Typography>
                        <Typography variant="body2" sx={{ color: COLORS.navy, opacity: 0.85, mt: 0.5 }}>{s.body}</Typography>
                        </Box>
                    </Card>
                    </Grid>
                ))}
                </Grid>
            </Container>

            {/* Closing Section */}
            <Box sx={{ bgcolor: COLORS.cream, py: 6 }}>
                    <Container maxWidth="md">
                    <Typography variant="h5" align="center" gutterBottom sx={{ color: COLORS.navy, fontWeight: 700 }}>
                        Built for creators, backed by reliable infrastructure.
                    </Typography>
                    <Typography variant="body1" align="center" sx={{ color: COLORS.navy, opacity: 0.85 }}>
                        Join the MonadX community to automate audience growth, launch high-converting DM funnels, and monetize your engagement on X.
                    </Typography>
                    </Container>
            </Box>
        </Box>
    )
}
export default AboutPage;